import Constants from '@/Constants';
import Tag from '@/models/Tag';
import { ApplicationState } from '@/store';
import { getTagsClear,getTagsRequest } from '@/store/tag/action';
import React,{ useEffect,useState } from 'react';
import { AsyncTypeahead,Typeahead } from 'react-bootstrap-typeahead';
import { useDispatch,useSelector } from 'react-redux';
import './style.scss';

interface Typeahead {
    [func: string]: CallableFunction;
}

interface Props {
    handleFill: string;
    setTags: CallableFunction;
    tags: Tag[];
}

const MyCreationTags: React.FC<Props> = (props: Props) => {
    const { handleFill, tags, setTags } = props;
    const { strings, initialTags, orgId, applicationId, suggestionTags } = useSelector((state: ApplicationState) => ({
        strings: state.locale.data,
        initialTags: state.tags.data.tags,
        orgId: state.identity.data.user.orgId,
        applicationId: state.identity.data.user.applicationId,
        suggestionTags: state.tags.data.suggestionTags,
    }));
    const [isFocused, setIsFocused] = useState(false);
    const [options, setOptions] = useState<Tag[]>([]);
    const [moduleTags, setModuleTags] = useState<Tag[]>([]);
    const refTypeahead: React.RefObject<Typeahead> = React.createRef();
    const NUMBER_OF_TAGS: number = 6;
    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(
            getTagsRequest({
                orgId,
                applicationId,
                limit: Constants.TAG_REQUEST_DEFAULT_LIMIT,
                offset: Constants.TAG_REQUEST_DEFAULT_OFFSET,
            }),
        );
        return () => {
            dispatch(getTagsClear());
        };
    }, [dispatch, orgId, applicationId]);

    useEffect(() => {
        if (suggestionTags && suggestionTags.length > 0) {
            setOptions(suggestionTags);
        }
    }, [suggestionTags]);

    useEffect(() => {
        if (tags !== moduleTags) {
            setTags(moduleTags);
        }
    }, [moduleTags]);

    useEffect(() => {
        if (tags.length > 0) setModuleTags(tags);
    }, [tags]);

    const handleSearch = (search: string) => {
        dispatch(
            getTagsRequest({
                orgId,
                applicationId,
                limit: Constants.TAG_REQUEST_DEFAULT_LIMIT,
                offset: Constants.TAG_REQUEST_DEFAULT_OFFSET,
                name: search,
                isSuggestion: true,
            }),
        );
    };

    const handleOnKeyDown = (tagName: string) => {
        if (tagName.trim().length === 0 || isTagAlreadySelected(tagName)) {
            refTypeahead && refTypeahead.current && refTypeahead.current.blur();
            clearInput();
            return;
        }

        const mergedList = initialTags.concat(options);

        const tagsFound: Tag[] = mergedList.filter((tag: Tag) => isTagNamesEqual(tagName, tag.name));

        if (tagsFound.length) {
            addExistingTag(tagsFound[0]);
        } else {
            setModuleTags((prevState): any => [
                ...prevState,
                {
                    applicationId: Constants.APP_ID,
                    id: null,
                    name: tagName,
                    organizationId: orgId,
                },
            ]);

            clearInput();
        }
    };

    const addExistingTag = (tag: Tag) => {
        setModuleTags((prevState): Tag[] => [...prevState, tag]);
        clearInput();
    };

    const clearInput = () => {
        refTypeahead && refTypeahead.current && refTypeahead.current.clear();
    };

    const isTagNamesEqual = (tag1: string, tag2: string): boolean => tag1.localeCompare(tag2, undefined, { sensitivity: 'base' }) === 0;

    const isTagAlreadySelected = (tagName: string): boolean => {
        return tags.findIndex((tag: Tag) => isTagNamesEqual(tagName, tag.name)) > -1;
    };

    const addNewTag = (tagToAdd: Tag) => {
        if (isTagAlreadySelected(tagToAdd.name)) {
            clearInput();
            return;
        }

        const tagsFound: Tag[] = initialTags && initialTags.filter((tag: Tag) => isTagNamesEqual(tagToAdd.name, tag.name));

        if (tagsFound.length) {
            addExistingTag(tagsFound[0]);
        } else {
            setModuleTags((prevState): any => [
                ...prevState,
                {
                    applicationId: Constants.APP_ID,
                    id: tagToAdd.id,
                    name: tagToAdd.name,
                    organizationId: tagToAdd.organizationId,
                },
            ]);

            clearInput();
        }
    };

    const handleOnChange = (typeaheadTagList: Tag[]) => {
        if (typeaheadTagList.length < tags.length) {
            setModuleTags(typeaheadTagList);
        } else {
            if (typeaheadTagList.length > 0 && typeaheadTagList[typeaheadTagList.length - 1].name) {
                const tagToAdd: Tag = typeaheadTagList[typeaheadTagList.length - 1] as Tag;

                addNewTag({
                    name: tagToAdd.name,
                    id: typeof tagToAdd.id !== 'number' ? null : tagToAdd.id,
                    organizationId: tagToAdd.organizationId || orgId,
                } as Tag);
            }
        }
    };

    const handleActiveTagStyle = (tag: Tag) => (tag && moduleTags && moduleTags.filter((t: Tag) => t.id === tag.id).length > 0 ? 'active' : '');

    const handleOnClick = (tag: Tag) => {
        if (moduleTags && moduleTags.filter((t: Tag) => t.id === tag.id).length <= 0) {
            addExistingTag(tag);
        }
    };

    const handleTagInputFocusStyle = () => {
        if (isFocused) {
            return 'focused';
        } else {
            return handleFill.length > 0 ? handleFill : 'blurred';
        }
    };

    return (
        <div className="tags-section">
            <div className="d-flex flex-wrap tags-input">
                <AsyncTypeahead
                    allowNew
                    className={`tag-typeahead-input ${handleTagInputFocusStyle()}`}
                    id="tag-input-typeahead"
                    isLoading={false}
                    labelKey="name"
                    minLength={3}
                    multiple
                    newSelectionPrefix={strings.ADD_NEW_TAG}
                    onBlur={() => setIsFocused(false)}
                    onChange={(tags: Tag[]) => handleOnChange(tags)}
                    onFocus={() => setIsFocused(true)}
                    onKeyDown={(event: KeyboardEvent) => event.key === 'Enter' && handleOnKeyDown((event.target as HTMLInputElement).value)}
                    onSearch={handleSearch}
                    options={options}
                    placeholder={strings.TYPE_TAGS}
                    positionFixed={true}
                    ref={refTypeahead}
                    selected={moduleTags}
                />
                <p className="info">{strings.TAGS_INFO}</p>
            </div>

            <div className="tag-options-section">
                <p className="options-title">{strings.SUGGESTED_TAGS}</p>
                <div className="mycreations-tags">
                    <div className="row-tags">
                        {(initialTags || []).map((tag: Tag, index: number) => {
                            if (index < NUMBER_OF_TAGS) {
                                return (
                                    <div
                                        className={`tag ${handleActiveTagStyle(tag)}`}
                                        key={tag.id}
                                        onClick={() => handleOnClick(tag)}
                                        onKeyDown={() => {}}
                                        role="button"
                                        tabIndex={0}
                                    >
                                        <span>{tag.name}</span>
                                    </div>
                                );
                            }
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MyCreationTags;
